import { useCallback, useEffect, useMemo, useState } from 'react';
import { useServices } from '@/components/ServiceContext';
import { FilterValueService } from '@/service/FilterValueService';
import { FilterValueRepository } from '@/repository/FilterValueRepository';
import { houseListFilters } from '@/pages/HouseListFilterPage';
import { FilterValue } from '@/API';
import { RelationalOperator } from '@lambda-layer/types/filter';
import { CountMapUtils, MLInactiveFlagsUtils } from '@/utils/CountMapUtils';
import log from 'loglevel';

interface MLInactiveFlag {
  key: string;
  docCount: number;
  originalCount?: number;
  isDefaultEnabled: boolean;
}

interface UseMLInactiveFlagsState {
  flags: MLInactiveFlag[];
  loading: boolean;
  updating: boolean;
  saving: boolean;
  hasChanges: boolean;
  currentFilter: FilterValue | null;
}

export const useMLInactiveFlags = () => {
  const { houseListService } = useServices();
  const [service] = useState(() => {
    const repository = new FilterValueRepository();
    return new FilterValueService(repository);
  });

  const [state, setState] = useState<UseMLInactiveFlagsState>({
    flags: [],
    loading: true,
    updating: false,
    saving: false,
    hasChanges: false,
    currentFilter: null,
  });

  const mlInactiveFlagsFilter = useMemo(() => {
    return houseListFilters.find(filter => filter.label === 'ML Active');
  }, []);

  const totalRemainingRecords = useMemo(() => {
    const countFlag = MLInactiveFlagsUtils.findTotalCountFlag(state.flags);
    return countFlag ? countFlag.docCount : 0;
  }, [state.flags]);

  const updateCounts = useCallback(
    async (currentFlags: MLInactiveFlag[]) => {
      if (!mlInactiveFlagsFilter) return currentFlags;

      setState(prev => ({ ...prev, updating: true }));

      try {
        const selectedFlags = MLInactiveFlagsUtils.getEnabledFlags(
          currentFlags
        ).map(flag => flag.key);
        const filterSelections =
          selectedFlags.length > 0
            ? {
                [mlInactiveFlagsFilter.id]: {
                  value: selectedFlags,
                  relationalOperator: RelationalOperator.CONTAINS_NONE,
                },
              }
            : {};

        const [countMap, originalCountMap] = await Promise.all([
          houseListService.getFilterCountMap(
            mlInactiveFlagsFilter.id,
            houseListFilters,
            filterSelections
          ),
          houseListService.getFilterCountMap(
            mlInactiveFlagsFilter.id,
            houseListFilters,
            {}
          ),
        ]);

        return currentFlags.map(flag => {
          if (CountMapUtils.isTotalCountKey(flag.key)) {
            return { ...flag, docCount: CountMapUtils.getTotalCount(countMap) };
          }

          return {
            ...flag,
            docCount: countMap.get(flag.key) || 0,
            originalCount: originalCountMap.get(flag.key) || 0,
          };
        });
      } catch (error) {
        log.error('Error updating counts:', error);
        throw error;
      } finally {
        setState(prev => ({ ...prev, updating: false }));
      }
    },
    [mlInactiveFlagsFilter, houseListService]
  );

  const loadFlags = useCallback(async () => {
    if (!mlInactiveFlagsFilter) return;

    setState(prev => ({ ...prev, loading: true }));

    try {
      const countMap = await houseListService.getFilterCountMap(
        mlInactiveFlagsFilter.id,
        houseListFilters,
        {}
      );

      const availableFlags = Array.from(countMap.entries())
        .filter(([key]) => key != null)
        .map(([key, docCount]) => ({
          key: key as string,
          docCount,
          isDefaultEnabled: false,
        }))
        .sort((a, b) => {
          if (a.key === 'count') return 1;
          if (b.key === 'count') return -1;
          return a.key.localeCompare(b.key);
        });

      const defaultFilters = await service.list();
      const latestFilter =
        defaultFilters.items
          ?.filter(
            (filter: FilterValue) =>
              filter.listType === 'houselist' &&
              filter.fieldId === mlInactiveFlagsFilter.id
          )
          .sort(
            (a, b) => parseInt(b.version || '0') - parseInt(a.version || '0')
          )[0] || null;

      const flagsWithDefaults = availableFlags.map(flag => ({
        ...flag,
        isDefaultEnabled:
          latestFilter?.selectedValues?.includes(flag.key) || false,
      }));

      const updatedFlags = await updateCounts(flagsWithDefaults);

      setState(prev => ({
        ...prev,
        flags: updatedFlags,
        currentFilter: latestFilter,
        hasChanges: false,
        loading: false,
      }));
    } catch (error) {
      log.error('Error loading flags:', error);
      setState(prev => ({ ...prev, loading: false }));
      throw error;
    }
  }, [mlInactiveFlagsFilter, houseListService, service, updateCounts]);

  const toggleFlag = useCallback(
    async (flagKey: string) => {
      const updatedFlags = state.flags.map(flag =>
        flag.key === flagKey
          ? { ...flag, isDefaultEnabled: !flag.isDefaultEnabled }
          : flag
      );

      const flagsWithUpdatedCounts = await updateCounts(updatedFlags);
      setState(prev => ({
        ...prev,
        flags: flagsWithUpdatedCounts,
        hasChanges: true,
      }));
    },
    [state.flags, updateCounts]
  );

  const selectAll = useCallback(async () => {
    const updatedFlags = state.flags.map(flag => ({
      ...flag,
      isDefaultEnabled: true,
    }));
    const flagsWithUpdatedCounts = await updateCounts(updatedFlags);
    setState(prev => ({
      ...prev,
      flags: flagsWithUpdatedCounts,
      hasChanges: true,
    }));
  }, [state.flags, updateCounts]);

  const clearAll = useCallback(async () => {
    const updatedFlags = state.flags.map(flag => ({
      ...flag,
      isDefaultEnabled: false,
    }));
    const flagsWithUpdatedCounts = await updateCounts(updatedFlags);
    setState(prev => ({
      ...prev,
      flags: flagsWithUpdatedCounts,
      hasChanges: true,
    }));
  }, [state.flags, updateCounts]);

  const saveChanges = useCallback(
    async (userEmail?: string) => {
      if (!mlInactiveFlagsFilter || !state.hasChanges) return;

      setState(prev => ({ ...prev, saving: true }));

      try {
        const enabledFlags = MLInactiveFlagsUtils.getEnabledFlags(
          state.flags
        ).map(flag => flag.key);
        const newVersion = state.currentFilter
          ? `${parseInt(state.currentFilter.version || '0') + 1}`
          : '1';

        const filterData = {
          listType: 'houselist',
          fieldId: mlInactiveFlagsFilter.id,
          relationalOperator: RelationalOperator.CONTAINS_NONE,
          selectedValues: enabledFlags,
          description: 'ML Inactive Flags configuration',
          version: newVersion,
          changedBy: userEmail || 'unknown',
        };

        const newFilter = await service.save(filterData);
        setState(prev => ({
          ...prev,
          currentFilter: newFilter,
          hasChanges: false,
          saving: false,
        }));

        return newFilter;
      } catch (error) {
        setState(prev => ({ ...prev, saving: false }));
        throw error;
      }
    },
    [
      mlInactiveFlagsFilter,
      state.flags,
      state.hasChanges,
      state.currentFilter,
      service,
    ]
  );

  const restoreVersion = useCallback(
    async (version: FilterValue) => {
      const updatedFlags = state.flags.map(flag => ({
        ...flag,
        isDefaultEnabled: version.selectedValues?.includes(flag.key) || false,
      }));

      const flagsWithUpdatedCounts = await updateCounts(updatedFlags);
      setState(prev => ({
        ...prev,
        flags: flagsWithUpdatedCounts,
        hasChanges: true,
      }));
    },
    [state.flags, updateCounts]
  );

  useEffect(() => {
    loadFlags();
  }, [loadFlags]);

  return {
    ...state,
    mlInactiveFlagsFilter,
    totalRemainingRecords,
    enabledCount: MLInactiveFlagsUtils.getEnabledFlags(state.flags).length,
    totalCount: MLInactiveFlagsUtils.filterOutCountFlags(state.flags).length,
    actions: {
      toggleFlag,
      selectAll,
      clearAll,
      saveChanges,
      restoreVersion,
      loadFlags,
    },
  };
};
